import { useState, useCallback, useEffect } from 'react';

function storageKey(userId: string) {
  return `pp_dismissed_task_notifs_${userId}`;
}

/**
 * Task rows shown in the notification bell/center are virtual (not DB rows).
 * Dismissing them only hides until the task is completed — stored in sessionStorage per user.
 */
export function useDismissedTaskNotificationIds(userId: string | null | undefined) {
  const [dismissedTaskIds, setDismissedTaskIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!userId) {
      setDismissedTaskIds(new Set());
      return;
    }
    try {
      const raw = sessionStorage.getItem(storageKey(userId));
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        setDismissedTaskIds(new Set(Array.isArray(arr) ? arr : []));
      } else {
        setDismissedTaskIds(new Set());
      }
    } catch {
      setDismissedTaskIds(new Set());
    }
  }, [userId]);

  const persist = useCallback(
    (next: Set<string>) => {
      if (!userId) return;
      try {
        if (next.size === 0) {
          sessionStorage.removeItem(storageKey(userId));
        } else {
          sessionStorage.setItem(storageKey(userId), JSON.stringify([...next]));
        }
      } catch {
        // ignore quota / private mode
      }
    },
    [userId]
  );

  const dismissTaskNotification = useCallback(
    (compositeId: string) => {
      if (!compositeId.startsWith('task-')) return false;
      const taskId = compositeId.slice('task-'.length);
      if (!taskId) return false;
      setDismissedTaskIds((prev) => {
        const next = new Set(prev);
        next.add(taskId);
        persist(next);
        return next;
      });
      return true;
    },
    [persist]
  );

  const clearDismissedTasks = useCallback(() => {
    setDismissedTaskIds(new Set());
    if (userId) {
      try {
        sessionStorage.removeItem(storageKey(userId));
      } catch {
        // ignore
      }
    }
  }, [userId]);

  const dismissAllTaskNotifications = useCallback(
    (taskIds: string[]) => {
      if (taskIds.length === 0) return;
      setDismissedTaskIds((prev) => {
        const next = new Set(prev);
        for (const id of taskIds) {
          if (id) next.add(id);
        }
        persist(next);
        return next;
      });
    },
    [persist],
  );

  return { dismissedTaskIds, dismissTaskNotification, clearDismissedTasks, dismissAllTaskNotifications };
}
